import React, { Component } from 'react';
import { compose } from 'utils/compose';
import { inject, observer } from 'mobx-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

import { Link as MLink, Box, Container, Button, Chip, Grid, Avatar, Typography } from '@mui/material';

import { useStyles } from 'containers/main/styles';
import withMediaQuery from 'constants/mediaQuery';
import FooterContainer from 'components/Footer';
import { getCookie } from 'utils/cookie';

// HOC to inject styles into class component
// no-dd-sa:javascript-code-style/parameter-name
const withUseStyles = (WrappedComponent) => {
  return function WithStylesWrapper(props) {
    const classes = useStyles();
    return <WrappedComponent {...props} classes={classes} />;
  };
};

class SignatureContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      signed: null,
      signing: false,
      checkingSignStatus: true,
    };
  }
  signature = null;
  resizeCanvas = () => {
    if (!this.signature) return;
    // When zoomed out to less than 100%, for some very strange reason,
    // some browsers report devicePixelRatio as less than 1
    // and only part of the canvas is cleared then.
    let ratio = Math.max(window.devicePixelRatio || 1, 1);
    const canvas = this.signature.getCanvas();

    if (!canvas) return;

    // This part causes the canvas to be cleared
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d').scale(ratio, ratio);

    // This library does not listen for canvas changes, so after the canvas is automatically
    // cleared by the browser, SignaturePad#isEmpty might still return false, even though the
    // canvas looks empty, because the internal data of this library wasn't cleared. To make sure
    // that the state of this library is consistent with visual state of the canvas, you
    // have to clear it manually.
    this.signature.clear();
  };

  componentWillUnmount() {
    window.removeEventListener('orientationchange', this.resizeCanvas);
  }

  componentDidMount() {
    const { store, location, match } = this.props;
    const { deliveryStore } = store;

    window.addEventListener('orientationchange', this.resizeCanvas);
    deliveryStore.getDelivery(match.params.tracking_code, (resp) => {
      if (resp.ok) {
        let token = location.search.replace('?token=', '');
        if (!token || token.trim() === '') {
          token = getCookie('axl_shipment_token');
        }
        store.deliveryStore.validateSignatureToken(token).then((resp) => {
          this.setState({ checkingSignStatus: false });
          if (resp.ok) {
            if (!resp.data) {
              this.gotoScan();
            } else {
              this.setState({ token });
            }
          } else {
            this.goBack();
          }
        });
      }
    });
  }

  goBack = () => {
    const { navigate, location } = this.props;
    this.setState({ signed: false });
    navigate(location.pathname.replace(/\/signature[^\.]*$/g, ''), { replace: true });
  };

  gotoScan = () => {
    const { navigate, location } = this.props;

    let token = location.search.replace('?token=', '');
    if (!token || token.trim() === '') {
      token = getCookie('axl_shipment_token');
    }

    this.setState({ signed: false });
    navigate(location.pathname.replace(/\/signature[^\.]*$/g, '/idscan?token=' + token), { replace: true });
  };

  sign = (e) => {
    const { store } = this.props;
    if (!this.state.signed) return;

    const trimmedCanvas = this.signature.getTrimmedCanvas();

    this.setState({ signing: true });
    store.deliveryStore.sign(trimmedCanvas.toDataURL(), this.state.token).then((resp) => {
      this.setState({ signing: false });
      if (resp.ok) {
        store.deliveryStore.delivery.signRequired = false;
        this.clear();
        if (store.deliveryStore.delivery?.shipment && store.deliveryStore.delivery.shipment.id_required) {
          this.gotoScan();
        } else {
          this.goBack();
        }
      }
    });
  };

  clear = (e) => {
    if (this.signature) {
      this.signature.clear();
      this.setState({ signed: false });
    }
  };

  onEnd = () => {
    this.setState({ signed: true });
  };

  render() {
    const { mediaQuery, classes, store } = this.props;
    const { delivery, trackingCode, settings, hideCompanyBranding } = store.deliveryStore;

    if (!delivery) return null;

    const { client, shipment } = delivery;

    return (
      <Container maxWidth={false} disableGutters sx={classes.container}>
        <Container sx={{ mt: 4, mb: 4, ...classes.bodyContainer }}>
          <Box mb={4} sx={classes.boxWrapper}>
            <Box p={mediaQuery.isMobile ? 2 : 4.5} pb={mediaQuery.isMobile ? 3 : undefined} sx={classes.statusContainer} borderRadius="10px 10px 0 0">
              <Box mb={2.5} sx={classes.statusTitle}>
                <Box>
                  <Box component="span" sx={classes.statusText}>
                    Status:{' '}
                  </Box>
                  <Box component="span" sx={classes.statusTextBig}>
                    Signature required
                  </Box>
                </Box>
                <Box>{shipment.attempt_count > 1 && <Chip size="small" sx={classes.reattemptButton} label={'2nd attempt'} />}</Box>
              </Box>
            </Box>
            <Grid container sx={classes.shipmentInfoContainer}>
              <Grid size={{ xs: 12, sm: 6 }} sx={classes.shipmentInfoLeft}>
                <Grid container spacing={2} style={{ marginBottom: 15 }}>
                  {!hideCompanyBranding && (
                    <Grid>
                      <Avatar alt="Client Logo" src={client.logo_url} variant="square" sx={classes.clientLogo} />
                    </Grid>
                  )}
                  <Grid>
                    {!hideCompanyBranding && (
                      <Typography sx={{ ...classes.clientName, ...classes.fontWeight700 }} variant="h4">
                        {client.company}
                      </Typography>
                    )}
                    <div>
                      <Box component="span" sx={classes.onSurfaceTextLabel}>
                        Tracking code:{' '}
                      </Box>
                      <Box component="span" sx={{ ...classes.onSurfaceTextTitle, ...classes.fontWeight600 }}>
                        {trackingCode}
                      </Box>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={12}>
                <Typography sx={classes.signTitle}>
                  To complete the delivery and confirm that you have received the shipment, please provide your signature in the box below:
                </Typography>
                <Typography style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                  *(You can also rotate your device horizontally to expand your signing space)
                </Typography>
                <Box display="flex" marginTop={1} alignItems="center" justifyContent="center" sx={classes.signPreviewBox} width="100%">
                  <SignatureCanvas
                    onEnd={this.onEnd}
                    ref={(ref) => {
                      this.signature = ref;
                    }}
                    penColor="black"
                    canvasProps={{ style: classes.canvas }}
                  />
                </Box>
                <Box marginTop={1} display="flex" justifyContent="flex-end">
                  <MLink href="#" onClick={this.clear}>
                    Clear
                  </MLink>
                </Box>
                <Box marginTop={5} display="flex" justifyContent="center">
                  <Button
                    disabled={!this.state.signed || this.state.signing}
                    color="primary"
                    onClick={this.sign}
                    fullWidth
                    sx={{ ...classes.button, ...classes.dialogButton, ...classes.signatureButton }}
                    variant="contained"
                  >
                    Confirm Signature
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
        {(!settings?.hide_footer || settings.hide_footer === 'false') && (
          <FooterContainer client={client} shipment={shipment} settings={settings} />
        )}
      </Container>
    );
  }
}

const SignatureCompose = compose(inject('store'), observer)(SignatureContainer);

// Wrapper component to provide router hooks to class component
function SignatureContainerWrapper(props) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  return <SignatureCompose {...props} match={{ params }} navigate={navigate} location={location} />;
}

export default withUseStyles(withMediaQuery()(SignatureContainerWrapper));
